import { Component, effect, EventEmitter, input, OnInit, output, Output, signal, ViewChild } from '@angular/core';
import {
  PatientBehaviorAntecedentModel,
  PatientBehaviorConsequenceModel,
  PatientBehaviorDataModel,
  PatientBehaviorLocationModel,
  PatientBehaviorModel,
  PatientBehaviorResponseModel,
  PatientBehaviorSeverityModel,
  PatientBehaviorType,
  PatientSessionModel,
  PatientTargetDataModel,
  PatientTargetModel,
  PromptCodeModel,
  TargetType,
} from '../../../core/api/models';
import { Utils } from '../../../core/utils';
import {
  StaffApiService,
  SupervisorApiService,
} from '../../../core/api/services';
import { AlertService } from '../../../shared/services/alert.service';
import { AuthService } from '../../../auth/auth.service';
import { PatientDataEditService } from '../../../core/services/patient-data-edit.service';
import { Table, TableRowCollapseEvent, TableRowExpandEvent } from 'primeng/table';
import { targetIsEchoic, targetIsPromptBased } from '../../../shared/utils/utils';
import { LoadingIndicatorComponent } from "../../../shared/components/loading-indicator/loading-indicator.component";
import * as FileSaver from 'file-saver';
import { forkJoin } from 'rxjs/internal/observable/forkJoin';

@Component({
  selector: 'app-rawdata-behavior',
  templateUrl: './rawdata-behavior.component.html',
  styleUrl: './rawdata-behavior.component.css',
  standalone: false,
})
export class RawDataBehaviorComponent implements OnInit {
  isLoading: boolean = true;
  savingData = false;

  rawDataBeingEdited: PatientBehaviorDataModel | null = null;
  clonedRawData: {
    [s: number]: PatientBehaviorDataModel;
  } = {};

  startDate: string = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().substring(0, 10);
  endDate: string = new Date().toISOString().substring(0, 10);
  showDeleted: boolean = false;
  showAddBehaviorData: boolean = false;

  behaviorsData: PatientBehaviorDataModel[] = [];
  selectedBehavior = input.required<PatientBehaviorModel>();

  behaviorDurationInMinutes = signal<number>(0);

  antecedents = signal<PatientBehaviorAntecedentModel[]>([]);
  consequences = signal<PatientBehaviorConsequenceModel[]>([]);
  locations = signal<PatientBehaviorLocationModel[]>([]);
  responses = signal<PatientBehaviorResponseModel[]>([]);
  severities = signal<PatientBehaviorSeverityModel[]>([]);

  selectedData: PatientBehaviorDataModel[] = [];

  @Output() onRawDataDeleted = new EventEmitter<boolean>();


  @ViewChild('dt') table!: Table;

  constructor(
    private staffApiService: StaffApiService,
    private supervisorApiService: SupervisorApiService,
    private alertService: AlertService,
    private authService: AuthService,
    private patientDataEditService: PatientDataEditService
  ) {
    effect(() => {
      this.isLoading = true;
      if (!this.selectedBehavior()?.ID) {
        this.isLoading = false;
        return;
      }
      this.loadData();
      this.loadBehaviorData();
    });
  }

  ngOnInit(): void {
    this.onRawDataDeleted.subscribe((deleted) => {
      this.loadData();
    });
  }

  loadData() {    
    //Load raw data
    this.supervisorApiService.GetBehaviorRawData({
      patientBehaviorID: this.selectedBehavior()?.ID!,
      startDate: this.startDate,
      endDate: this.endDate,
    }).subscribe((response) => {
      this.behaviorsData = response ?? [];
      if (!this.showDeleted) {
        this.behaviorsData = this.behaviorsData.filter((t) => !t.DeletedDate);
      }
      this.isLoading = false;
    });
  }

  
  loadBehaviorData() {
    const antecedentRequest = this.staffApiService.GetPatientBehaviorAntecedents({ patientID: this.selectedBehavior()?.PatientID ?? 0 });
    const consequenceRequest = this.staffApiService.GetPatientBehaviorConsequences({ patientID: this.selectedBehavior()?.PatientID ?? 0 });
    const severityRequest = this.staffApiService.GetPatientBehaviorSeverities({});
    const responseRequest = this.staffApiService.GetPatientBehaviorResponses({});
    const locationRequest = this.staffApiService.GetPatientBehaviorLocations({});

    forkJoin({
      antecedents: antecedentRequest, 
      consequences: consequenceRequest,
      severities: severityRequest,
      responses: responseRequest,
      locations: locationRequest
    }).subscribe({
      next: (results) => {

        this.antecedents.set(results.antecedents);
        this.consequences.set(results.consequences);
        this.severities.set(results.severities);
        this.responses.set(results.responses);
        this.locations.set(results.locations);

      },
      error: (error) => {
        this.alertService.error('Error loading behavior data');
      }
    });
  }

  filterData() {
    this.loadData();
  }

  addData() {
    this.showAddBehaviorData = true;
  }

  onCloseAddData(event: any) {
    this.showAddBehaviorData = false;
    this.loadData();
  }

  hideAddData() {
    this.showAddBehaviorData = false;
  }

  secondsToFormattedString(seconds: number) {
    return Utils.formatTime(seconds);
  }

  minutesToSeconds(minutes: number): number {
    return Math.round(minutes * 60);
  }

  secondsToMinutes(seconds: number): number {
    return seconds / 60;
  }

  minutesToFormattedString(minutes: number): string {
    const seconds = this.minutesToSeconds(minutes);
    return this.secondsToFormattedString(seconds);
  }

  behaviorIsDuration(): boolean {
    return this.selectedBehavior()?.BehaviorType === PatientBehaviorType.Duration;
  }

  behaviorIsIntensity(): boolean {
    return this.selectedBehavior()?.BehaviorType === PatientBehaviorType.Intensity;
  }

  intensityOptions() {
    const behavior = this.selectedBehavior();
    return Utils.intensityScaleValues(
      behavior?.IntensityScaleMin,
      behavior?.IntensityScaleMax,
      behavior?.IntensityScaleValues
    ).map((value) => ({ label: value.toString(), value }));
  }


  onRawDataEditInit(
    rawData: PatientBehaviorDataModel
  ) {
    console.log('Editing raw data:', rawData);

    this.rawDataBeingEdited = rawData;

    if (this.behaviorIsDuration()) {
      this.behaviorDurationInMinutes.set(this.secondsToMinutes(rawData.IntValue ?? 0));
    }

    this.clonedRawData[rawData.ID as number] = Utils.deepClone(rawData);
  }

  onRawDataEditSave(
    rawData: PatientBehaviorDataModel
  ) {
    if (this.savingData) return;
    if (this.behaviorIsDuration()) {
      const intValueInSeconds = this.minutesToSeconds(this.behaviorDurationInMinutes());
      const textValue = this.minutesToFormattedString(this.behaviorDurationInMinutes());
      
      // Update the passed object
      rawData.IntValue = intValueInSeconds;
      rawData.TextValue = textValue;
      
      // Also update the object in the array to ensure it's saved correctly
      const index = this.behaviorsData.findIndex((b) => b.ID === rawData.ID);
      if (index !== -1) {
        this.behaviorsData[index].IntValue = intValueInSeconds;
        this.behaviorsData[index].TextValue = textValue;
      }
    }
    if (this.behaviorIsIntensity()) {
      if (rawData.IntValue !== null && rawData.IntValue !== undefined) {
        rawData.TextValue = rawData.IntValue.toString();
      }
    }

    console.log('Updated raw data:', rawData);
    console.log('IntValue being saved:', rawData.IntValue);
    this.saveRawData(rawData);
  }

  onRawDataDelete(
    rawData: PatientBehaviorDataModel
  ) {
    console.log('Deleting raw data:', rawData);
    this.alertService
      .confirm(
        'Delete Data',
        'Are you sure you want to delete this data?'
      )
      .subscribe((result) => {
        if (result) {
          this.deleteRawData(rawData);
        }
      });
  }

  onRawDataRestore(
    rawData: PatientBehaviorDataModel
  ) {
    console.log('Restoring raw data:', rawData);
    this.restoreRawData(rawData);
  }

  onRawDataEditCancel(
    rawData: PatientBehaviorDataModel
  ) {
    console.log('Cancelled editing raw data:', rawData);

    const index = this.behaviorsData?.findIndex(
      (t) => t.ID === rawData.ID
    );
    if (index === undefined) {
      console.error('Could not find index for raw data:', rawData);
      return;
    }

    this.behaviorsData![index] = this.clonedRawData[
      rawData.ID as number
    ] as PatientBehaviorDataModel;
    delete this.clonedRawData[rawData.ID as number];

  }

  saveRawData(newRawData: PatientBehaviorDataModel) {
    this.isLoading = true;
    this.savingData = true;
    console.log('Saving raw data:', newRawData);
    
    const currentUser = this.authService.userInfo;
    const currentUserID = currentUser?.ID;
    if (!newRawData.UserID && currentUserID) {
      newRawData.UserID = currentUserID;
      newRawData.UserFullName = currentUser?.Fullnane ?? '';
    }
    
    // Note: ModifiedByUserID, ModifiedDate, ManuallyAddedByUserID, and ManuallyAddedDate 
    // are all set by the backend and should not be set here
    
    const original = this.clonedRawData[newRawData.ID as number];
    this.patientDataEditService
      .save(newRawData, original, (submitted) => this.staffApiService.SavePatientBehaviorData({
        addedManually: true,
        body: submitted,
      }))
      .subscribe(
        (response) => {
          console.log('Saved raw data:', response);
          
          this.alertService.success('Raw data saved successfully');
          this.isLoading = false;
          this.savingData = false;
          delete this.clonedRawData[newRawData.ID as number];
          
          // Reload the data list to get the latest from the server
          this.loadData();
        },
        (error) => {
          console.error('Error saving raw data:', error);
          if (original) {
            Object.assign(newRawData, original);
            this.onRawDataEditCancel(newRawData);
          }
          this.alertService.error(
            'Error saving raw data - ' + Utils.getAjaxError(error)
          );
          this.isLoading = false;
          this.savingData = false;
        }
      );
  }

  deleteRawData(
    rawData: PatientTargetDataModel | PatientBehaviorDataModel
  ) {
    this.isLoading = true;
    console.log('Deleting raw data:', rawData);
    if (rawData.ID) {
      this.staffApiService
        .MarkBehaviorDataAsDeleted({
          patientBehaviorDataID: rawData.ID,
        })
        .subscribe((response) => {
          console.log('Behavior data deleted:', response);
          this.isLoading = false;
          this.onRawDataDeleted.emit(true);
        });
    }
  }

  restoreRawData(
    rawData: PatientBehaviorDataModel
  ) {
    this.isLoading = true;
    console.log('Deleting raw data:', rawData);
    if (rawData.ID) {
      this.staffApiService
        .RestoreBehaviorDataFromDelete({
          patientBehaviorDataID: rawData.ID,
        })
        .subscribe((response) => {
          console.log('Behavior data restored:', response);
          this.isLoading = false;
          this.onRawDataDeleted.emit(true);
        });
    }
  }

  getExportData(): any[] {
    const dataToExport = (this.table?.filteredValue) || this.behaviorsData;

    return dataToExport.map((behaviorData) => {
        return {
          'Session': this.getSessionInfo(behaviorData),
          'Recorded Date': this.formatDate(behaviorData.RecordedDate),
          'Value': this.getDisplayValue(behaviorData),
          'Created By': behaviorData.UserFullName ?? '',
          'Modified By': behaviorData.ModifiedByUser ? this.getUserNameFromViewModel(behaviorData.ModifiedByUser) : 'N/A',
          'Modified Date': behaviorData.ModifiedDate ? this.formatDate(behaviorData.ModifiedDate) : '',
          // Add other fields as needed
        };
    });
  }

  formatDate(date: Date): string {
    return new Date(date).toLocaleString(); // Adjust formatting as needed
  }

  getDisplayValue(behaviorData: PatientBehaviorDataModel): string {
    if (this.behaviorIsIntensity()) {
      return behaviorData.IntValue?.toString() ?? '';
    }
    if (this.behaviorIsDuration() && behaviorData.IntValue != null) {
      return this.secondsToFormattedString(behaviorData.IntValue);
    }
    return behaviorData.TextValue ?? '';
  }

  getSessionInfo(behaviorData: PatientBehaviorDataModel): string {
    const session = behaviorData.SessionData;
    if (session) {
      const started = session.StartedDate ? this.formatDate(new Date(session.StartedDate)) : '';
      const finished = session.FinishedDate ? this.formatDate(new Date(session.FinishedDate)) : '';
      const serviceID = session.ServiceID ? `Service: #${session.ServiceID}` : '';
      return `Session on ${started} - ${finished} ${serviceID}`;
    }
    return '';
  }

  exportExcel() {
    import('xlsx').then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(this.getExportData());
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = xlsx.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });
      this.saveAsExcelFile(excelBuffer, 'table_data');
    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    FileSaver.saveAs(
      data,
      fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION
    );
  }

  //BEHAVIORS

  getBehaviorName(behaviorId: number) {
    const behavior = this.selectedBehavior();
    return behavior?.PatientBehaviorName ?? 'Unknown';
  }

  getAntecedentsLabelsName(
    antecedentIDs: number[],
    antecedentOther: string | null
  ) {
    if (antecedentIDs.length === 0 && !antecedentOther) {
      return 'None';
    }
    var antecedentsLabel = antecedentIDs
      .map((antecedentId) => {
        const antecedent = this.antecedents().find(
          (a) => a.ID === antecedentId
        );
        return antecedent?.PatientBehaviorAntecedent ?? 'Unknown';
      })
      .join(', ');
    if (antecedentOther) {
      if (antecedentsLabel === '') {
        antecedentsLabel += antecedentOther;
      } else {
        antecedentsLabel += ', ' + antecedentOther;
      }
    }
    return antecedentsLabel;
  }

  getConsequencesLabelsName(
    consequenceIDs: number[],
    consequenceOther: string | null
  ) {
    if (consequenceIDs.length === 0 && !consequenceOther) {
      return 'None';
    }
    var consequenceLabel = consequenceIDs
      .map((consequenceId) => {
        const consequence = this.consequences().find(
          (c) => c.ID === consequenceId
        );
        return consequence?.PatientBehaviorConsequence ?? 'Unknown';
      })
      .join(', ');
    if (consequenceOther) {
      if (consequenceLabel === '') {
        consequenceLabel += consequenceOther;
      } else {
        consequenceLabel += ', ' + consequenceOther;
      }
    }
    return consequenceLabel;
  }

  getLocationName(locationId: number | null) {
    if (!locationId) {
      return 'None';
    }
    const location = this.locations().find((l) => l.ID === locationId);
    return location?.PatientBehaviorLocation ?? 'Unknown';
  }

  getResponseName(responseId: number | null) {
    if (!responseId) {
      return 'None';
    }
    const response = this.responses().find((r) => r.ID === responseId);
    return response?.PatientBehaviorResponse ?? 'Unknown';
  }

  getSeverityName(severityId: number | null) {
    if (!severityId) {
      return 'None';
    }
    const severity = this.severities().find((s) => s.ID === severityId);
    return severity?.PatientBehaviorSeverity ?? 'Unknown';
  }



  behaviorAntecedentsOptions() {
    const antecedentsOptions = this.antecedents().map((a) => {
      return {
        value: a.ID,
        text: a.PatientBehaviorAntecedent,
      };
    });
    return antecedentsOptions;
  }

  behaviorConsequencesOptions() {
    const consequencesOptions = this.consequences().map((c) => {
      return {
        value: c.ID,
        text: c.PatientBehaviorConsequence,
      };
    });
    return consequencesOptions;
  }

  behaviorLocationsOptions() {
    const locationsOptions = this.locations().map((l) => {
      return {
        value: l.ID,
        text: l.PatientBehaviorLocation,
      };
    });
    return locationsOptions;
  }

  behaviorResponsesOptions() {
    const responsesOptions = this.responses().map((r) => {
      return {
        value: r.ID,
        text: r.PatientBehaviorResponse,
      };
    });
    return responsesOptions;
  }

  behaviorSeveritiesOptions() {
    const severitiesOptions = this.severities().map((s) => {
      return {
        value: s.ID,
        text: s.PatientBehaviorSeverity,
      };
    });
    return severitiesOptions;
  }


  onBehaviorsRowExpand(event: TableRowExpandEvent) {
    console.log('Row expanded:', event);
  }

  onBehaviorsRowCollapse(event: TableRowCollapseEvent) {
    console.log('Row collapsed:', event);
  }

  formatManuallyAddedDate(date: string | null | undefined): string {
    if (!date) return '';
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: '2-digit' 
    });
  }

  get isAnyRowEditing(): boolean {
    return Object.keys(this.clonedRawData).length > 0;
  }
  
  getUserNameFromViewModel(user: any): string {
    if (!user) return 'N/A';
    if (user.FirstName || user.LastName) {
      return `${user.FirstName || ''} ${user.LastName || ''}`.trim() || user.UserName || 'N/A';
    }
    return user.UserName || 'N/A';
  }
  
  getUserDisplayName(userID: number | null | undefined, userFullName: string | null | undefined): string {
    // Use UserFullName if available, otherwise return empty string (will fallback to N/A in template)
    return userFullName || '';
  }

}
